import { OFFLINE_BOOKING, space, BOOKING } from '../../../api/apiPaths';
import { 
  get, 
  post, 
  put, 
  del,
  createOfflineBooking as createOfflineBookingAPI,
  createOfflineBookingPayment,
  updateOfflineBookingPayment,
  getOfflineBookingPayment,
  updateBookingStatus,
  cancelBooking as cancelBookingAPI
} from '../../../api/commonAPI';
import axios from 'axios';
import { message } from 'antd';
import * as actionTypes from './OfflineBookingActionTypes';
import { baseURL } from '../../../settings';
import { userPath } from '../../../api/apiPaths';

// Fetch All Spaces for dropdown with pagination and search support
export const fetchAllSpaces = (page = 1, size = 20, searchQuery = '', country = 'LK', appendResults = false) => {
  return (dispatch) => {
    dispatch({ type: actionTypes.FETCH_SPACES_FOR_BOOKING });

    // Build URL with proper query parameters as per the API specification
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      dataView: 'TABLE_VIEW',
      status: 'ACTIVE',
      country: country
    });

    // Add search query if provided
    if (searchQuery && searchQuery.trim()) {
      params.append('q', searchQuery.trim());
    }

    const url = `${space}?${params.toString()}`;
    
    console.log('Fetching spaces:', url);
    
    get(url)
      .then((response) => {
        if (response && response.status >= 200 && response.status < 300 && response.data) {
          let spaceList = [];
          let totalPages = 0;
          let totalElements = 0;
          
          // Handle different response structures
          if (Array.isArray(response.data)) {
            spaceList = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            spaceList = response.data.data;
          } else if (response.data.content && Array.isArray(response.data.content)) {
            spaceList = response.data.content;
            totalPages = response.data.totalPages || 0;
            totalElements = response.data.totalElements || spaceList.length;
          }
          
          const dropdownSpaces = spaceList.map(space => ({
            id: space.spaceId || space.id,
            spaceName: space.spaceName || space.name || space.title,
            name: space.spaceName || space.name || space.title,
            title: space.spaceName || space.name || space.title,
            location: {
              city: space.location?.city || space.city || '',
              address: space.location?.address || space.address || '',
              country: space.location?.country || space.country || ''
            },
            status: space.spaceStatus || space.status,
            events: space.events || [],
            host: space.host || space.spaceOwner,
            category: space.category,
            organizationName: space.organizationName
          }));
          
          // Filter to ensure only ACTIVE spaces
          const activeSpaces = dropdownSpaces.filter(s => {
            const status = s.status;
            return !status || status === 'ACTIVE';
          });
          
          dispatch({
            type: actionTypes.FETCH_SPACES_FOR_BOOKING_SUCCESS,
            payload: activeSpaces,
            meta: {
              page,
              size,
              totalPages,
              totalElements,
              appendResults,
              searchQuery
            }
          });
        } else {
          dispatch({
            type: actionTypes.FETCH_SPACES_FOR_BOOKING_FAIL,
            payload: 'Invalid response'
          });
        }
      })
      .catch((error) => {
        console.error('Error fetching spaces:', error);
        dispatch({
          type: actionTypes.FETCH_SPACES_FOR_BOOKING_FAIL,
          payload: error.message || 'Failed to fetch spaces'
        });
      });
  };
};

// Search spaces by query (debounced search for dropdown)
export const searchSpaces = (searchQuery = '', page = 1, size = 20, country = 'LK') => {
  return (dispatch) => {
    dispatch({ type: actionTypes.SEARCH_SPACES_FOR_BOOKING });

    // If search query is empty, fetch all spaces
    if (!searchQuery || searchQuery.trim().length === 0) {
      return dispatch(fetchAllSpaces(page, size, '', country, false));
    }

    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      dataView: 'TABLE_VIEW',
      status: 'ACTIVE',
      country: country,
      q: searchQuery.trim()
    });

    const url = `${space}?${params.toString()}`;
    
    console.log('Searching spaces:', url);
    
    get(url)
      .then((response) => {
        if (response && response.status >= 200 && response.status < 300 && response.data) {
          let spaceList = [];
          let totalPages = 0;
          let totalElements = 0;
          
          if (Array.isArray(response.data)) {
            spaceList = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            spaceList = response.data.data;
          } else if (response.data.content && Array.isArray(response.data.content)) {
            spaceList = response.data.content;
            totalPages = response.data.totalPages || 0;
            totalElements = response.data.totalElements || spaceList.length;
          }
          
          const dropdownSpaces = spaceList.map(space => ({
            id: space.spaceId || space.id,
            spaceName: space.spaceName || space.name || space.title,
            name: space.spaceName || space.name || space.title,
            title: space.spaceName || space.name || space.title,
            location: {
              city: space.location?.city || space.city || '',
              address: space.location?.address || space.address || '',
              country: space.location?.country || space.country || ''
            },
            status: space.spaceStatus || space.status,
            events: space.events || [],
            host: space.host || space.spaceOwner,
            category: space.category,
            organizationName: space.organizationName
          }));
          
          const activeSpaces = dropdownSpaces.filter(s => {
            const status = s.status;
            return !status || status === 'ACTIVE';
          });
          
          dispatch({
            type: actionTypes.SEARCH_SPACES_FOR_BOOKING_SUCCESS,
            payload: activeSpaces,
            meta: {
              page,
              size,
              totalPages,
              totalElements,
              searchQuery
            }
          });
        } else {
          dispatch({
            type: actionTypes.SEARCH_SPACES_FOR_BOOKING_SUCCESS,
            payload: [],
            meta: {
              page,
              size,
              totalPages: 0,
              totalElements: 0,
              searchQuery
            }
          });
        }
      })
      .catch((error) => {
        console.error('Error searching spaces:', error);
        dispatch({
          type: actionTypes.SEARCH_SPACES_FOR_BOOKING_FAIL,
          payload: error.message || 'Failed to search spaces'
        });
      });
  };
};

// Fetch Space Details by ID
export const fetchSpaceById = (spaceId) => {
  return (dispatch) => {
    dispatch({ type: actionTypes.FETCH_SPACE_BY_ID });

    get(`${space}/${spaceId}`)
      .then((response) => {
        // Handle axios error objects returned by get() helper
        if (!response || typeof response.status !== 'number') {
          console.error('Error fetching space details:', response);
          dispatch({
            type: actionTypes.FETCH_SPACE_BY_ID_FAIL,
            payload: 'Error fetching space details'
          });
          message.error('Error fetching space details');
          return;
        }

        if (response.status >= 200 && response.status < 300 && response.data) {
          dispatch({
            type: actionTypes.FETCH_SPACE_BY_ID_SUCCESS,
            payload: response.data
          });
        } else {
          dispatch({
            type: actionTypes.FETCH_SPACE_BY_ID_FAIL,
            payload: 'Error fetching space details'
          });
          message.error('Error fetching space details');
        }
      })
      .catch((error) => {
        console.error('Error fetching space details:', error);
        dispatch({
          type: actionTypes.FETCH_SPACE_BY_ID_FAIL,
          payload: error?.message || 'Failed to fetch space details'
        });
        message.error(error?.message || 'Failed to fetch space details');
      });
  };
};

// Create Offline Booking via Backend API
export const createOfflineBooking = (data) => {
  return (dispatch) => {
    dispatch({ type: actionTypes.CREATE_OFFLINE_BOOKING });

    // Get token from localStorage
    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

    console.log('Creating offline booking with data:', data);
    console.log('Token present:', !!token);

    return createOfflineBookingAPI(data, token)
      .then((response) => {
        console.log('Booking created successfully:', response);
        const bookingData = response.data || response;
        
        dispatch({
          type: actionTypes.CREATE_OFFLINE_BOOKING_SUCCESS,
          payload: bookingData
        });
        
        return { 
          payload: bookingData,
          success: true 
        };
      })
      .catch((error) => {
        console.error('Error creating offline booking:', error);
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response?.data);
        console.error('Error status:', error.response?.status);
        console.error('Error message:', error.message);
        
        dispatch({
          type: actionTypes.CREATE_OFFLINE_BOOKING_FAIL,
          payload: error.message || 'Failed to create offline booking'
        });
        
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error || 
                           error.message || 
                           'Error saving booking. Please try again';
        
        return { 
          error: errorMessage,
          success: false 
        };
      });
  };
};

// Fetch All Offline Bookings
export const fetchOfflineBookings = () => {
  return (dispatch) => {
    dispatch({ type: actionTypes.FETCH_OFFLINE_BOOKINGS });

    // Get token from localStorage, cookies, or use axios interceptor
    let token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    
    // Try to get from cookies
    if (!token) {
      const cookies = document.cookie.split(';').find(c => c.trim().startsWith('token='));
      if (cookies) {
        token = cookies.split('=')[1];
      }
    }

    // Use dedicated backend API endpoint for fetching bookings by type
    // Backend requires admin authorization
    // URL: http://localhost:8080/api/booking/byBookingType
    const url = `${baseURL}api/booking/byBookingType`;
    
    console.log('Fetching offline bookings from:', url);
    console.log('Token:', token ? `Present (${token.substring(0, 20)}...)` : 'Missing - will use axios interceptor');
    
    axios.get(url, {
      params: {
        bookingType: 'OFFLINE_BOOKING',
        page: 0,
        size: 100
      },
      headers: token ? {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : {
        'Content-Type': 'application/json'
      }
    })
      .then((response) => {
        console.log('Offline bookings fetched successfully:', response.data);
        
        if (response && (response.status === 200 || response.status === 201)) {
          let bookingList = [];
          
          // Handle different response structures
          if (Array.isArray(response.data)) {
            bookingList = response.data;
          } else if (response.data?.content && Array.isArray(response.data.content)) {
            bookingList = response.data.content;
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            bookingList = response.data.data;
          }
          
          // Filter to ensure only OFFLINE_BOOKING type are included
          const offlineBookings = bookingList.filter(
            booking => booking.bookingType === 'OFFLINE_BOOKING'
          );
          
          console.log('Filtered offline bookings:', offlineBookings);
          
          dispatch({
            type: actionTypes.FETCH_OFFLINE_BOOKINGS_SUCCESS,
            payload: offlineBookings
          });
        } else {
          dispatch({
            type: actionTypes.FETCH_OFFLINE_BOOKINGS_FAIL,
            payload: 'Error fetching offline bookings'
          });
        }
      })
      .catch((error) => {
        console.error('Error fetching offline bookings:', error);
        console.error('Error URL:', url);
        console.error('Error Status:', error.response?.status);
        console.error('Error Data:', error.response?.data);
        
        dispatch({
          type: actionTypes.FETCH_OFFLINE_BOOKINGS_FAIL,
          payload: error.message || 'Failed to fetch offline bookings'
        });
      });
  };
};

// Update Offline Booking
export const updateOfflineBooking = (id, data) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.UPDATE_OFFLINE_BOOKING });

    try {
      // First, fetch the booking to get current version for optimistic locking
      const bookingResponse = await axios.get(`${baseURL}api/booking/${id}`);
      const currentBooking = bookingResponse.data;
      
      if (!currentBooking) {
        throw new Error('Booking not found');
      }
      
      // Get version from ETag header or booking data
      const version = bookingResponse.headers.etag || currentBooking.version;
      
      console.log('Updating booking - ID:', id, 'Version:', version);
      
      // Merge current booking with new data
      const updatedBooking = {
        ...currentBooking,
        ...data
      };
      
      // Update the booking with If-Match header for optimistic locking
      const response = await axios.put(
        `${baseURL}api/booking`,
        updatedBooking,
        {
          headers: {
            'If-Match': version,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        dispatch({
          type: actionTypes.UPDATE_OFFLINE_BOOKING_SUCCESS,
          payload: response.data
        });
      } else {
        dispatch({
          type: actionTypes.UPDATE_OFFLINE_BOOKING_FAIL,
          payload: 'Error updating offline booking'
        });
        message.error('Error updating booking');
      }
    } catch (error) {
      console.error('Error updating offline booking:', error);
      console.error('Error response:', error.response);
      
      const errorMsg = error.response?.data?.message || 
                      error.response?.data?.error || 
                      error.message || 
                      'Failed to update offline booking';
      
      dispatch({
        type: actionTypes.UPDATE_OFFLINE_BOOKING_FAIL,
        payload: errorMsg
      });
      message.error(errorMsg);
    }
  };
};

// Delete Offline Booking
export const deleteOfflineBooking = (id) => {
  return (dispatch) => {
    dispatch({ type: actionTypes.DELETE_OFFLINE_BOOKING });

    // Use BOOKING endpoint for delete
    del(`${BOOKING}${id}`)
      .then((response) => {
        if (response.status === 200) {
          dispatch({
            type: actionTypes.DELETE_OFFLINE_BOOKING_SUCCESS,
            payload: id
          });
          message.success('Offline booking deleted successfully');
        } else {
          dispatch({
            type: actionTypes.DELETE_OFFLINE_BOOKING_FAIL,
            payload: 'Error deleting offline booking'
          });
          message.error('Error deleting booking');
        }
      })
      .catch((error) => {
        console.error('Error deleting offline booking:', error);
        dispatch({
          type: actionTypes.DELETE_OFFLINE_BOOKING_FAIL,
          payload: error.message || 'Failed to delete offline booking'
        });
        message.error('Error deleting booking');
      });
  };
};

// Fetch Space Bookings for Calendar - similar to ms-web implementation
export const fetchSpaceBookings = (spaceId) => {
  return (dispatch) => {
    dispatch({ type: actionTypes.FETCH_SPACE_BOOKINGS });

    get(`${BOOKING}space/${spaceId}/bookings`)
      .then((response) => {
        // Handle axios error objects returned by get() helper
        if (!response || typeof response.status !== 'number') {
          const status = response?.response?.status;
          if (status === 404) {
            // Treat 404 as "no bookings" for this space (not an error)
            dispatch({
              type: actionTypes.FETCH_SPACE_BOOKINGS_SUCCESS,
              payload: []
            });
            return;
          }
          // Real error: show toast and dispatch FAIL
          dispatch({
            type: actionTypes.FETCH_SPACE_BOOKINGS_FAIL,
            payload: 'Error fetching space bookings'
          });
          message.error('Error fetching space bookings');
          return;
        }

        // Accept any 2xx response
        if (response.status >= 200 && response.status < 300) {
          const processedBookings = Array.isArray(response.data) 
            ? response.data.map(booking => ({
                id: booking.id,
                date: new Date(booking.bookingStartDate || booking.startDate).toISOString().split('T')[0],
                startTime: new Date(booking.bookingStartDate || booking.startDate).toTimeString().slice(0, 5),
                endTime: new Date(booking.bookingEndDate || booking.endDate).toTimeString().slice(0, 5),
                status: booking.status || 'CONFIRMED',
                type: booking.bookingType || (booking.isOffline ? 'OFFLINE_BOOKING' : 'ONLINE_BOOKING'),
                clientName: booking.user?.name || booking.clientName || 'Unknown',
                bookingRef: booking.bookingRef || booking.id
              })) 
            : [];
          
          dispatch({
            type: actionTypes.FETCH_SPACE_BOOKINGS_SUCCESS,
            payload: processedBookings
          });
        } else {
          // Non-2xx: dispatch FAIL and show error
          dispatch({
            type: actionTypes.FETCH_SPACE_BOOKINGS_FAIL,
            payload: 'Error fetching space bookings'
          });
          message.error('Error fetching space bookings');
        }
      })
      .catch((error) => {
        console.error('Error fetching space bookings:', error);
        dispatch({
          type: actionTypes.FETCH_SPACE_BOOKINGS_FAIL,
          payload: error?.message || 'Error fetching space bookings'
        });
        message.error(error?.message || 'Error fetching space bookings');
      });
  };
};

// Fetch Calendar Data for a space (similar to ms-web getCalenderData)
// This API returns bookings grouped by date with all booking types
export const getCalendarData = (spaceId, params, headers, callback) => {
  return (dispatch) => {
    dispatch({ type: actionTypes.FETCH_CALENDAR_DATA });

    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    const url = `${baseURL}api/booking/calendar/${spaceId}?startDate=${params.startDate}&endDate=${params.endDate}`;
    
    console.log('Fetching calendar data from:', url);
    
    axios.get(url, {
      headers: headers || (token ? {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : {
        'Content-Type': 'application/json'
      })
    })
      .then((response) => {
        console.log('Calendar data fetched successfully:', response.data);
        
        if (response && (response.status === 200 || response.status === 201)) {
          dispatch({
            type: actionTypes.FETCH_CALENDAR_DATA_SUCCESS,
            payload: response.data
          });
          if (callback) callback(true, response.data);
        } else {
          dispatch({
            type: actionTypes.FETCH_CALENDAR_DATA_FAIL,
            payload: 'Error fetching calendar data'
          });
          if (callback) callback(false, null);
        }
      })
      .catch((error) => {
        console.error('Error fetching calendar data:', error);
        console.error('Error response:', error.response);
        
        dispatch({
          type: actionTypes.FETCH_CALENDAR_DATA_FAIL,
          payload: error.message || 'Failed to fetch calendar data'
        });
        if (callback) callback(false, error);
      });
  };
};

// Clear error messages
export const clearOfflineBookingError = () => ({
  type: actionTypes.CLEAR_OFFLINE_BOOKING_ERROR
});

// Reset form
export const resetOfflineBookingForm = () => ({
  type: actionTypes.RESET_OFFLINE_BOOKING_FORM
});

// Clear space details (when changing space selection)
export const clearSpaceDetails = () => ({
  type: actionTypes.CLEAR_SPACE_DETAILS
});

/**
 * Client (User) search for Offline Booking
 * Uses userservice endpoint: GET api/user?text={query}&page=0&size=10
 * Requires admin Authorization header. Returns 404 when no results – treat as empty list.
 */
export const searchClients = (query, page = 0, size = 10) => {
  return async (dispatch) => {
    if (!query || query.trim().length < 2) {
      // don't spam API for very short queries
      dispatch({ type: actionTypes.CLEAR_CLIENT_SUGGESTIONS });
      return;
    }

    dispatch({ type: actionTypes.SEARCH_CLIENTS });

    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const url = `${baseURL}${userPath}?text=${encodeURIComponent(query)}&page=${page}&size=${size}`;
      const response = await axios.get(url, { headers });

      // Endpoint returns 200 with array; 404 when empty (handled in catch)
      const data = Array.isArray(response.data) ? response.data : (response.data?.content || []);

      dispatch({
        type: actionTypes.SEARCH_CLIENTS_SUCCESS,
        payload: data,
      });
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        dispatch({ type: actionTypes.SEARCH_CLIENTS_SUCCESS, payload: [] });
      } else if (status === 401) {
        dispatch({ type: actionTypes.SEARCH_CLIENTS_FAIL, payload: 'Unauthorized to search users' });
      } else {
        dispatch({ type: actionTypes.SEARCH_CLIENTS_FAIL, payload: error?.message || 'Failed to search clients' });
      }
    }
  };
};

export const clearClientSuggestions = () => ({ type: actionTypes.CLEAR_CLIENT_SUGGESTIONS });

/**
 * Fetch a single client's full details by ID
 * Uses base CRUD endpoint provided by userservice controller (BaseController)
 * GET api/user/{id}
 */
export const fetchClientById = (userId) => {
  return async () => {
    if (!userId) return null;
    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const url = `${baseURL}${userPath}/${userId}`;
    try {
      const res = await axios.get(url, { headers });
      return res?.data || null;
    } catch (err) {
      return null;
    }
  };
};

/**
 * Payment Management Actions for Offline Bookings
 */

// Create Payment for Offline Booking
export const createPaymentForOfflineBooking = (paymentData) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.CREATE_OFFLINE_PAYMENT });

    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

    console.log('Action: Creating payment with token:', token ? 'Present' : 'Missing');
    console.log('Action: Payment data:', paymentData);

    try {
      const response = await createOfflineBookingPayment(paymentData, token);
      console.log('Action: Payment API response:', response);
      
      // response is already the data (not response.data)
      const paymentRecord = response;
      
      dispatch({
        type: actionTypes.CREATE_OFFLINE_PAYMENT_SUCCESS,
        payload: paymentRecord
      });
      
      // If payment status is COMPLETED, update booking status to CONFIRMED
      // The scheduler will automatically change CONFIRMED -> COMPLETED after booking end date
      if (paymentData.paymentStatus === 'COMPLETED' && paymentData.bookingId) {
        try {
          const updateResponse = await updateBookingStatus(paymentData.bookingId, 'CONFIRMED');
          console.log('Booking status updated to CONFIRMED in backend:', updateResponse);
        } catch (bookingError) {
          console.error('Failed to update booking status in backend:', bookingError);
          console.error('Booking error response:', bookingError);
        }
      }
      
  // success notification handled by calling component to avoid duplicates
      
      return { 
        payload: paymentRecord,
        success: true 
      };
    } catch (error) {
      console.error('Action: Error creating payment:', error);
      console.error('Action: Error response:', error.response);
      console.error('Action: Error data:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          error.response?.data ||
                          error.message || 
                          'Failed to record payment';
      
      dispatch({
        type: actionTypes.CREATE_OFFLINE_PAYMENT_FAIL,
        payload: errorMessage
      });
      
  message.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to record payment');
      
      return { 
        error: errorMessage,
        success: false 
      };
    }
  };
};

// Update Payment Status for Offline Booking
export const updatePaymentStatusForOfflineBooking = (paymentId, updateData) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.UPDATE_OFFLINE_PAYMENT });

    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

    try {
      const response = await updateOfflineBookingPayment(paymentId, updateData, token);
      
      dispatch({
        type: actionTypes.UPDATE_OFFLINE_PAYMENT_SUCCESS,
        payload: response.data || response
      });
      
      // If payment status is COMPLETED, update booking status to CONFIRMED
      // The scheduler will automatically change CONFIRMED -> COMPLETED after booking end date
      if (updateData.paymentStatus === 'COMPLETED' && updateData.bookingId) {
        try {
          const updateResponse = await updateBookingStatus(updateData.bookingId, 'CONFIRMED');
          console.log('Booking status updated to CONFIRMED in backend:', updateResponse);
        } catch (bookingError) {
          console.error('Failed to update booking status in backend:', bookingError);
          console.error('Booking error response:', bookingError);
        }
      }
      
  // success notification handled by calling component to avoid duplicates
      return { payload: response.data || response };
    } catch (error) {
      console.error('Error updating payment:', error);
      console.error('Error response:', error.response);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to update payment';
      
      dispatch({
        type: actionTypes.UPDATE_OFFLINE_PAYMENT_FAIL,
        payload: errorMessage
      });
      message.error(errorMessage);
      return { error: errorMessage };
    }
  };
};

// Fetch Payment Details for Offline Booking
export const fetchPaymentForOfflineBooking = (bookingId) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.FETCH_OFFLINE_PAYMENT });

    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

    try {
      const response = await getOfflineBookingPayment(bookingId, token);
      dispatch({
        type: actionTypes.FETCH_OFFLINE_PAYMENT_SUCCESS,
        payload: response.data || response
      });
      return { 
        payload: response.data || response,
        success: true 
      };
    } catch (error) {
      console.error('Error fetching payment:', error);
      dispatch({
        type: actionTypes.FETCH_OFFLINE_PAYMENT_FAIL,
        payload: error.message || 'No payment found'
      });
      return { 
        error: error.message || 'No payment found',
        success: false 
      };
    }
  };
};

// Fetch Booking Availability
export const fetchBookingAvailability = (spaceId, eventTypeId, startDate, endDate, chargingType) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.FETCH_BOOKING_AVAILABILITY });

    try {
      const response = await axios.get(`${baseURL}api/booking/availability`, {
        params: {
          space: spaceId,
          eventTypeId,
          startDate,
          endDate,
          chargingType
        }
      });

      dispatch({
        type: actionTypes.FETCH_BOOKING_AVAILABILITY_SUCCESS,
        payload: response.data || []
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching booking availability:', error);
      dispatch({
        type: actionTypes.FETCH_BOOKING_AVAILABILITY_FAIL,
        payload: error.response?.data?.message || error.message || 'Failed to fetch availability'
      });
      throw error;
    }
  };
};

// Clear Booking Availability
export const clearBookingAvailability = () => ({
  type: actionTypes.CLEAR_BOOKING_AVAILABILITY
});

// Confirm Offline Booking - Update booking status to CONFIRMED
export const confirmOfflineBooking = (bookingId) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.CONFIRM_OFFLINE_BOOKING });

    try {
      console.log('Confirming offline booking:', bookingId);
      
      // Use the updateBookingStatus API from commonAPI
      const updatedBooking = await updateBookingStatus(bookingId, 'CONFIRMED');
      
      console.log('Booking confirmed successfully:', updatedBooking);
      
      // Also update payment status to COMPLETED when booking is confirmed
      try {
        const paymentResponse = await getOfflineBookingPayment(bookingId);
        
        if (paymentResponse && paymentResponse.id) {
          console.log('Payment found, updating status to COMPLETED');
          // Build auth header explicitly to satisfy pay-service admin check
          const rawToken = localStorage.getItem('adminToken') || localStorage.getItem('token') || '';
          const authHeader = rawToken
            ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`)
            : undefined;

          const updateBody = { paymentStatus: 'COMPLETED' };

          // Prefer direct axios call with headers to avoid interceptor issues
          await axios.put(
            `${baseURL}api/pay/offline-booking/${paymentResponse.id}`,
            updateBody,
            {
              headers: authHeader ? { Authorization: authHeader } : {}
            }
          );
          console.log('Payment status updated to COMPLETED');
        }
      } catch (paymentError) {
        // If no payment found or update failed, log but don't fail the booking confirmation
        console.error('Payment update skipped:', paymentError?.response?.data || paymentError?.message || paymentError);
      }
      
      dispatch({
        type: actionTypes.CONFIRM_OFFLINE_BOOKING_SUCCESS,
        payload: updatedBooking
      });

      return { 
        payload: updatedBooking,
        success: true 
      };
    } catch (error) {
      console.error('Error confirming offline booking:', error);
      
      dispatch({
        type: actionTypes.CONFIRM_OFFLINE_BOOKING_FAIL,
        payload: error.message || 'Failed to confirm booking'
      });

      return { 
        error: error.message || 'Failed to confirm booking',
        success: false 
      };
    }
  };
};

// Cancel Offline Booking - Update booking status to CANCELLED
export const cancelOfflineBooking = (bookingId) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.CANCEL_OFFLINE_BOOKING });

    try {
      console.log('Cancelling offline booking:', bookingId);
      
      // Use the dedicated cancelBooking API which handles payment completed scenarios
      const updatedBooking = await cancelBookingAPI(bookingId);
      
      console.log('Booking cancelled successfully:', updatedBooking);
      
      dispatch({
        type: actionTypes.CANCEL_OFFLINE_BOOKING_SUCCESS,
        payload: updatedBooking
      });

      return { 
        payload: updatedBooking,
        success: true 
      };
    } catch (error) {
      console.error('Error cancelling offline booking:', error);
      
      const errorMessage = typeof error === 'string' ? error : error.message || 'Failed to cancel booking';
      
      dispatch({
        type: actionTypes.CANCEL_OFFLINE_BOOKING_FAIL,
        payload: errorMessage
      });

      return { 
        error: errorMessage,
        success: false 
      };
    }
  };
};
